import React, { useEffect, useState, useMemo } from "react";
import Layout from "../components/app/Layout";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../contexts/I18nContext";
import api from "../lib/api";
import { Link } from "react-router-dom";
import { Users, Calendar as CalIcon, Calendar, MessageSquare, ArrowRight, Trophy, MapPin, Clock, Sparkles, Star, Settings } from "lucide-react";
import ActivityFeed from "../components/app/ActivityFeed";
import MapPreview from "../components/app/MapPreview";
import AuthImage from "../components/app/AuthImage";
import Avatar from "../components/app/Avatar";
import CountUp from "../components/app/CountUp";
import EmptyState from "../components/app/EmptyState";

function Stat({ label, value, sub, icon: Icon, testId, delay = 0 }) {
  const numeric = typeof value === "number";
  return (
    <div
      className="stat-tile animate-fade-up"
      style={{ animationDelay: `${delay}ms`, opacity: 0 }}
      data-testid={testId}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="stat-label">{label}</div>
          <div className="stat-number mt-2">
            {numeric ? <CountUp value={value} /> : value}
          </div>
          {sub && <div className="stat-delta up"><span>▲</span>{sub}</div>}
        </div>
        <div className="relative">
          <div className="absolute inset-0 bg-pitch blur-2xl opacity-25" />
          <div className="w-10 h-10 rounded-full bg-pitch/8 border border-pitch/30 flex items-center justify-center relative">
            <Icon className="w-4 h-4 text-pitch" />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [children, setChildren] = useState([]);
  const [trainings, setTrainings] = useState([]);
  const [matches, setMatches] = useState([]);

  useEffect(() => {
    (async () => {
      const [c, tr, m] = await Promise.all([
        api.get("/children"),
        api.get("/trainings"),
        api.get("/matches"),
      ]);
      setChildren(c.data);
      setTrainings(tr.data);
      setMatches(m.data);
    })();
  }, []);

  const upcoming = useMemo(
    () => trainings.filter((tr) => new Date(tr.start_at) > new Date()).slice(0, 5),
    [trainings]
  );
  const next = upcoming[0];

  const upcomingMatches = useMemo(
    () => matches.filter((m) => new Date(m.start_at) > new Date()).slice(0, 3),
    [matches]
  );

  const thisWeek = useMemo(() => {
    const now = new Date();
    const start = new Date(now);
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(end.getDate() + 7);
    return trainings.filter((tr) => {
      const d = new Date(tr.start_at);
      return d >= start && d <= end;
    }).length;
  }, [trainings]);

  const dateFmt = (iso) =>
    new Date(iso).toLocaleString(lang === "bg" ? "bg-BG" : "en-US", {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });

  const heroImg =
    "https://images.unsplash.com/photo-1517747614396-d21a78b850e8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

  return (
    <Layout>
      <div className="space-y-6" data-testid="dashboard-root">
        {/* Hero */}
        <div className="relative card-sharp overflow-hidden grain animate-fade-up" style={{ animationDelay: "0ms", opacity: 0 }}>
          <div className="spotlight" />
          <div
            className="absolute inset-0 opacity-20"
            style={{ background: `linear-gradient(90deg, #050505 0%, rgba(5,5,5,0.6) 60%, transparent 100%), url("${heroImg}") center/cover` }}
          />
          <div className="absolute inset-0 pitch-field opacity-30" />
          <div className="absolute top-0 line-sweep" />
          <div className="relative p-6 md:p-8 flex flex-col md:flex-row gap-6 items-start">
            {user?.role === "coach" && (
              <Avatar name={user.name} src={user.photo_url} size="2xl" ring className="flex-shrink-0" />
            )}
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-3.5 h-3.5 text-pitch" />
                <div className="label-mono text-pitch">
                  // {user?.role === "coach" ? t("coach") : user?.role === "academy" ? (lang === "bg" ? "Академия" : "Academy") : t("parent")} COMMAND CENTER
                </div>
              </div>
              <h1 className="font-oswald uppercase text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none">
                {t("welcome")},<br />
                <span className="text-pitch">{user?.name?.split(" ")[0]}</span>
              </h1>
              {user?.role === "coach" && (
                <div className="flex flex-wrap items-center gap-3 mt-3">
                  {(user.rating || user.review_count) ? (
                    <div className="flex items-center gap-1">
                      <Star className="w-3.5 h-3.5 text-pitch fill-pitch" />
                      <span className="font-oswald">{(user.rating || 0).toFixed(1)}</span>
                      <span className="text-xs text-white/40">({user.review_count || 0})</span>
                    </div>
                  ) : null}
                  {user.city && <span className="text-sm text-white/60 flex items-center gap-1"><MapPin className="w-3 h-3 text-pitch" />{user.city}</span>}
                  {user.specialty && <span className="tag tag-pitch">{user.specialty}</span>}
                  <Link to="/settings" className="text-xs font-mono text-white/40 hover:text-pitch transition-colors flex items-center gap-1">
                    <Settings className="w-3 h-3" /> {lang === "bg" ? "Редактирай" : "Edit"}
                  </Link>
                </div>
              )}
              {user?.bio ? (
                <p className="text-white/60 mt-3 max-w-2xl text-sm">{user.bio}</p>
              ) : (
                <p className="text-white/50 mt-3 max-w-xl text-sm md:text-base">{t("heroSub")}</p>
              )}
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Stat
            label={user?.role === "coach" ? t("totalPlayers") : t("myChildren")}
            value={children.length}
            icon={Users}
            testId="stat-children"
            delay={50}
          />
          <Stat label={t("totalTrainings")} value={trainings.length} icon={CalIcon} testId="stat-trainings" delay={100} />
          <Stat label={t("weeklyTrainings")} value={thisWeek} sub={t("thisWeek")} icon={Sparkles} testId="stat-weekly" delay={150} />
          <Stat label={t("upcomingMatches")} value={upcomingMatches.length} icon={Trophy} testId="stat-matches" delay={200} />
        </div>

        {/* Main grid: trainings + sidebar */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Trainings list */}
          <div className="lg:col-span-2 card-sharp p-6 animate-fade-up" style={{ animationDelay: "200ms", opacity: 0 }}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="label-mono mb-1">// schedule</div>
                <h2 className="font-oswald uppercase text-2xl tracking-tight">{t("upcomingTrainings")}</h2>
              </div>
              <Link to="/calendar" className="text-pitch text-xs font-mono uppercase tracking-widest hover:underline flex items-center gap-1" data-testid="link-calendar">
                {t("calendar")} <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
            {upcoming.length === 0 ? (
              <EmptyState
                icon={Calendar}
                title={lang === "bg" ? "Няма предстоящи тренировки" : "No upcoming trainings"}
                description={lang === "bg" ? "Планирай първата сесия от календара." : "Schedule your first session from the calendar."}
                cta={lang === "bg" ? "Към календара" : "Open calendar"}
                ctaTo="/calendar"
                testId="empty-trainings"
                compact
              />
            ) : (
              <div className="space-y-2">
                {upcoming.map((tr, i) => (
                  <div
                    key={tr.id}
                    className="group border border-white/5 hover:border-pitch/40 transition-colors p-4 grid grid-cols-[auto_1fr_auto] gap-4 items-center animate-fade-up"
                    style={{ animationDelay: `${250 + i * 50}ms`, opacity: 0 }}
                    data-testid={`training-row-${tr.id}`}
                  >
                    <div className="text-center border-r border-white/5 pr-4">
                      <div className="font-oswald text-3xl text-pitch leading-none">
                        {new Date(tr.start_at).getDate()}
                      </div>
                      <div className="label-mono mt-1">
                        {new Date(tr.start_at).toLocaleString(lang === "bg" ? "bg-BG" : "en-US", { month: "short" })}
                      </div>
                    </div>
                    <div className="min-w-0">
                      <div className="font-oswald uppercase tracking-tight truncate">{tr.title}</div>
                      <div className="flex flex-wrap gap-3 text-xs text-white/50 mt-1">
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3 text-pitch" /> {dateFmt(tr.start_at)}</span>
                        {tr.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3 text-pitch" /> {tr.location}</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Sidebar: activity */}
          <div className="space-y-4">
            <div className="animate-fade-up" style={{ animationDelay: "300ms", opacity: 0 }}>
              <ActivityFeed limit={6} />
            </div>
          </div>
        </div>

        {/* Upcoming matches */}
        <div className="card-sharp p-6 animate-fade-up" style={{ animationDelay: "350ms", opacity: 0 }}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Trophy className="w-4 h-4 text-pitch" />
              <h2 className="font-oswald uppercase text-2xl tracking-tight">{t("upcomingMatches")}</h2>
            </div>
            <Link to="/matches" className="text-pitch text-xs font-mono uppercase tracking-widest hover:underline flex items-center gap-1">
              {t("matches")} <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          {upcomingMatches.length === 0 ? (
            <EmptyState
              icon={Trophy}
              title={lang === "bg" ? "Няма предстоящи мачове" : "No upcoming matches"}
              description={lang === "bg" ? "Добавяй мачове, за да следиш формата на отбора." : "Add matches to track team form."}
              cta={lang === "bg" ? "Към мачове" : "View matches"}
              ctaTo="/matches"
              testId="empty-matches"
              compact
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {upcomingMatches.map((m) => (
                <div key={m.id} className="border border-white/5 hover:border-pitch/40 transition-colors p-4" data-testid={`match-card-${m.id}`}>
                  <div className="label-mono mb-2 flex items-center justify-between">
                    <span>{m.is_home ? t("home") : t("away")}</span>
                    <span className="text-pitch">{new Date(m.start_at).toLocaleDateString(lang === "bg" ? "bg-BG" : "en-US", { day: "2-digit", month: "short" })}</span>
                  </div>
                  <div className="font-oswald uppercase text-xl tracking-tight">{t("vs")} {m.opponent}</div>
                  {m.location && <div className="text-xs text-white/40 mt-1 flex items-center gap-1"><MapPin className="w-3 h-3" />{m.location}</div>}
                  {m.location_lat && m.location_lng && <div className="mt-3"><MapPreview lat={m.location_lat} lng={m.location_lng} height={100} /></div>}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Players preview */}
        <div className="card-sharp p-6 animate-fade-up" style={{ animationDelay: "400ms", opacity: 0 }}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-oswald uppercase text-2xl tracking-tight">
              {user?.role === "coach" ? (lang === "bg" ? "Моите играчи" : "My players") : t("myChildren")}
            </h2>
            <Link to="/children" className="text-pitch text-xs font-mono uppercase tracking-widest hover:underline">
              {t("children")} →
            </Link>
          </div>
          {children.length === 0 ? (
            <EmptyState
              icon={Users}
              title={user?.role === "coach"
                ? (lang === "bg" ? "Все още няма играчи" : "No players yet")
                : (lang === "bg" ? "Все още няма деца" : "No children yet")}
              description={user?.role === "coach"
                ? (lang === "bg" ? "Добави първия играч от секцията Играчи." : "Add your first player from the Players section.")
                : (lang === "bg" ? "Добави първото дете, за да следиш развитието му." : "Add your first child to track progress.")}
              cta={lang === "bg" ? "Добави" : "Add now"}
              ctaTo="/children"
              testId="empty-children"
              compact
            />
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
              {children.slice(0, 12).map((c) => (
                <Link
                  to={c.is_player_user ? `/player/${c.id}` : `/children/${c.id}`}
                  key={c.id}
                  className="border border-white/5 hover:border-pitch/40 transition-colors p-3 group rounded-md"
                  data-testid={`child-card-${c.id}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="w-10 h-10 bg-pitch text-white font-oswald flex items-center justify-center text-lg group-hover:scale-105 transition-transform rounded">
                      {c.jersey_number || c.name?.[0]}
                    </div>
                    {c.position && <span className="pos-badge">{c.position.slice(0, 2).toUpperCase()}</span>}
                  </div>
                  <div className="font-oswald uppercase tracking-tight text-sm truncate">{c.name}</div>
                  <div className="text-xs text-white/40">
                    {c.age != null ? `${c.age}${lang === "bg" ? "г." : "y."}` : (c.is_player_user ? (lang === "bg" ? "Профил" : "Profile") : "—")}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
